import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import IntegerField from '../../../../core/components/Fields/IntegerField.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import FieldPathInput from '../components/FieldPathInput.tsx';
import { useActionUpdate } from '../hooks/useActionUpdate.ts';
import type { TransformAction, TransformFunction, TransformFunctionInfo, AliasContextEntry } from '../types.ts';

interface TransformBlockProps {
    action: TransformAction;
    mappingIndex: number;
    actionIndex: number;
    aliasContext?: AliasContextEntry[];
    onUpdate?: (updates: Partial<TransformAction>) => void;
}

const FunctionParamInput = ({
    param,
    value,
    onChange,
}: {
    param: TransformFunctionInfo['params'][0];
    value: any;
    onChange: (value: any) => void;
}) => {
    switch (param.type) {
        case 'number':
            return (
                <IntegerField
                    value={value ?? ''}
                    onValueChange={(v) => onChange(v ? Number(v) : undefined)}
                    placeholder={param.description}
                />
            );
        case 'boolean':
            return (
                <SelectField
                    value={value ? 'true' : 'false'}
                    onValueChange={(v) => onChange(v === 'true')}
                    options={[
                        { value: 'true', label: 'True' },
                        { value: 'false', label: 'False' },
                    ]}
                />
            );
        case 'object':
            return (
                <textarea
                    value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || ''}
                    onChange={(e) => {
                        try {
                            onChange(JSON.parse(e.target.value));
                        } catch {
                            onChange(e.target.value);
                        }
                    }}
                    className="w-full h-20 px-2 py-1.5 text-sm border border-line-weak rounded bg-surface-weak text-content-strong font-mono resize-none"
                    placeholder={param.description || '{"key": "value"}'}
                />
            );
        case 'array':
            return (
                <StringField
                    value={Array.isArray(value) ? value.join(', ') : value || ''}
                    onValueChange={(v) => onChange(v.split(',').map((s) => s.trim()))}
                    placeholder={param.description || 'value1, value2, value3'}
                />
            );
        default:
            return <StringField value={value ?? ''} onValueChange={onChange} placeholder={param.description} />;
    }
};

const TransformBlock = ({ action, mappingIndex, actionIndex, aliasContext = [], onUpdate }: TransformBlockProps) => {
    const { t } = useTranslation('integrations');
    const { transformFunctions } = useOutputMapperEditor();
    const handleUpdate = useActionUpdate<TransformAction>(mappingIndex, actionIndex, onUpdate);

    const selectedFunction = useMemo(() => {
        return transformFunctions.find((f) => f.name === action.transformation_function);
    }, [action.transformation_function, transformFunctions]);

    const functionOptions = useMemo(() => {
        const grouped: Record<string, typeof transformFunctions> = {};
        transformFunctions.forEach((f) => {
            if (!grouped[f.category]) grouped[f.category] = [];
            grouped[f.category].push(f);
        });

        const options: { value: string; label: string; description?: string }[] = [];
        const categoryLabels: Record<string, string> = {
            string: 'String',
            date: 'Date',
            number: 'Number',
            logic: 'Logic',
        };

        Object.entries(grouped).forEach(([category, funcs]) => {
            options.push({
                value: `__${category}`,
                label: `--- ${categoryLabels[category]} ---`,
                description: '',
            });
            funcs.forEach((f) => {
                options.push({
                    value: f.name,
                    label: f.label,
                    description: f.description,
                });
            });
        });

        return options;
    }, [transformFunctions]);

    const handleFunctionChange = (funcName: string) => {
        if (funcName.startsWith('__')) return;
        handleUpdate({
            transformation_function: funcName as TransformFunction,
            function_params: {},
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <div>
                <label className="text-xs text-content-weak mb-1 block">
                    {t('outputMapper.visualEditor.function')}
                </label>
                <SelectField
                    value={action.transformation_function}
                    onValueChange={handleFunctionChange}
                    options={functionOptions}
                    renderOption={(option) => (
                        <div>
                            <div className={option.value.startsWith('__') ? 'font-bold text-content-weak' : ''}>
                                {option.label}
                            </div>
                            {option.description && (
                                <div className="text-xs text-content-weak">{option.description}</div>
                            )}
                        </div>
                    )}
                />
            </div>

            {selectedFunction && (
                <div className="text-xs text-content-weak bg-surface-medium p-2 rounded">
                    {selectedFunction.description}
                </div>
            )}

            <div>
                <label className="text-xs text-content-weak mb-1 block">
                    {t('outputMapper.visualEditor.sourceOptional')}
                </label>
                <FieldPathInput
                    value={action.source || ''}
                    onChange={(source) => handleUpdate({ source: source || undefined })}
                    aliasContext={aliasContext}
                    placeholder={t('outputMapper.visualEditor.usesChainValue')}
                />
            </div>

            {selectedFunction && selectedFunction.params.length > 0 && (
                <div className="flex flex-col gap-2 pl-3 border-l-2 border-line-weak">
                    <div className="text-xs font-medium text-content-weak">
                        {t('outputMapper.visualEditor.parameters')}
                    </div>
                    {selectedFunction.params.map((param) => (
                        <div key={param.name}>
                            <label className="text-xs text-content-weak mb-1 block">
                                {param.name}
                                {param.required && <span className="text-status-danger-medium ml-1">*</span>}
                            </label>
                            <FunctionParamInput
                                param={param}
                                value={action.function_params?.[param.name]}
                                onChange={(value) =>
                                    handleUpdate({
                                        function_params: {
                                            ...action.function_params,
                                            [param.name]: value,
                                        },
                                    })
                                }
                            />
                            {action.transformation_function === 'format_date' && param.name === 'format' && (
                                <p className="text-xs text-content-weak mt-1">
                                    {t('outputMapper.visualEditor.formatDateHint')}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TransformBlock;
